import { compose } from '@reduxjs/toolkit';

import type { Middleware } from './rtk-types';
import { storeManager } from './storeManager';
import type { GetStoreFn, InjectMiddlewareFn } from './types';

export function createDynamicMiddleware(moduleName: string, getStore: GetStoreFn) {
    /**
     * Список мидлвар, которые были подключены к стору в рантайме.
     */
    const dynamicMiddlewares: Middleware[] = [];

    /**
     * Добавляет мидлвару в модульный стор.
     *
     * @param middleware – Мидлвара, которую нужно добавить.
     */
    const injectMiddleware: InjectMiddlewareFn = middleware => {
        dynamicMiddlewares.push(middleware);
    };

    /**
     * Redux-мидлвара, которая запускает все мидлвары сохраненные в dynamicMiddlewares.
     * Нужна, чтобы иметь возможность добавлять новые мидлвары в рантайме
     */
    const dynamicMiddleware: Middleware = api => next => action => {
        getStore();

        const lastComposedMiddlewareIndex = storeManager.getLastComposedMiddlewareIndex(moduleName) ?? -1;
        let composedDynamicMiddleware = storeManager.getComposedDynamicMiddleware(moduleName) ?? next;

        /**
         * Если есть мидлвары, которые еще не были добавлены в композицию
         */
        if (lastComposedMiddlewareIndex < dynamicMiddlewares.length - 1) {
            const middlewaresToCompose = dynamicMiddlewares.slice(lastComposedMiddlewareIndex + 1);
            const appliedMiddlewaresToCompose = middlewaresToCompose.map(middleware => middleware(api));

            /**
             * Мидлвары композируются в обратном порядке: `compose(m2, m1)(next) => m2(m1(next))`.
             * Последняя добавленная мидлвара должна быть самой внешней - необходим `reverse`;
             */
            appliedMiddlewaresToCompose.reverse();

            /**
             * Цепочка мидлвар собирается следующим образом: `mw2(mw1(next))`
             * При добавлении новой мидлвары mw3 нужно завернуть текущую композицию в новую мидлвару:
             * `mw3(mw2(mw1(next)))`
             */
            composedDynamicMiddleware = compose<ReturnType<ReturnType<Middleware>>>(...appliedMiddlewaresToCompose)(
                composedDynamicMiddleware
            );

            storeManager.setComposedDynamicMiddleware(moduleName, composedDynamicMiddleware);
            storeManager.setLastComposedMiddlewareIndex(moduleName, dynamicMiddlewares.length - 1);
        }

        return composedDynamicMiddleware(action);
    };

    return { dynamicMiddleware, injectMiddleware };
}
